// Client for the browser-upload endpoint (CONTRACTS §2 — POST /publish).
// The file bytes are the raw request body; metadata rides in the query string.
// XHR is used (not fetch) so the dialog can show real upload progress. The
// __session cookie authorizes the call (withCredentials). Uploaded docs are
// created trusted:false server-side → the viewer sandboxes their scripts.

export interface UploadResult {
  docId: string;
  versionId: string;
  title: string;
  fileCount: number;
  bytes: number;
}

export interface UploadOptions {
  title?: string;
  folderId?: string | null;
  onProgress?: (fraction: number) => void;
}

/** Upload a single .html or a .zip bundle as a new doc. */
export function uploadDoc(file: File, opts: UploadOptions = {}): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams();
    params.set("filename", file.name);
    if (opts.title) params.set("title", opts.title);
    if (opts.folderId) params.set("folderId", opts.folderId);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/publish?${params.toString()}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/octet-stream");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new Error("Network error during upload."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResult);
        } catch {
          reject(new Error("Upload succeeded but the response was unreadable."));
        }
        return;
      }
      let message = `Upload failed (${xhr.status}).`;
      if (xhr.status === 401) message = "Your session expired — sign in again.";
      else if (xhr.status === 403) message = "You cannot upload to that folder.";
      else {
        try {
          const data = JSON.parse(xhr.responseText) as { error?: string };
          if (data?.error) message = data.error;
        } catch {
          /* keep the generic message */
        }
      }
      reject(new Error(message));
    };

    xhr.send(file);
  });
}

/** True for file types the uploader accepts. */
export function isUploadable(file: File): boolean {
  return /\.(html?|zip)$/i.test(file.name);
}
